import { useEffect, useLayoutEffect, useRef, useState } from 'react';

const reduced = () => typeof window !== 'undefined'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Once-per-mount entrance state. Returns `pending=true` only when JS is running,
 * motion is allowed and the element starts below the viewport. Elements already
 * on screen, or when JS fails, render in their final state — never hidden.
 */
export default function useEntrance<T extends HTMLElement>(threshold = 0.25) {
  const ref = useRef<T>(null);
  const [pending, setPending] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || reduced() || typeof IntersectionObserver === 'undefined') return;
    const r = el.getBoundingClientRect();
    if (r.top < window.innerHeight * 0.9) return;
    setPending(true);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el || !pending) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setPending(false);
        io.disconnect();
      }
    }, { threshold, rootMargin: '0px 0px -8% 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [pending, threshold]);

  return { ref, pending };
}